import { useState, useEffect } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useTenant } from '../contexts/TenantContext';
import { AuthLayout } from '../layouts/AuthLayout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import { Loader2, AlertCircle, CheckCircle2 } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const InvitationPage = () => {
    const { orgCode } = useParams();
    const [searchParams] = useSearchParams();
    const { tenant } = useTenant();
    const navigate = useNavigate();
    
    const [validating, setValidating] = useState(true);
    const [valid, setValid] = useState(false);
    const [invitationData, setInvitationData] = useState(null);
    const [error, setError] = useState('');
    
    const [name, setName] = useState('');
    const [password, setPassword] = useState('');
    const [loading, setLoading] = useState(false);
    
    const token = searchParams.get('token');

    useEffect(() => {
        if (token) {
            validateInvitation();
        } else {
            setValidating(false);
            setError('Kein Einladungstoken gefunden');
        }
    }, [token]);

    const validateInvitation = async () => {
        try {
            const response = await axios.get(`${API}/invitations/validate/${token}`);
            setInvitationData(response.data);
            setValid(true);
            if (response.data.name) {
                setName(response.data.name);
            }
        } catch (err) {
            setError(err.response?.data?.detail || 'Ungültige Einladung');
            setValid(false);
        } finally {
            setValidating(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (password.length < 6) {
            toast.error('Das Passwort muss mindestens 6 Zeichen lang sein');
            return;
        }
        
        setLoading(true);

        try {
            const response = await axios.post(`${API}/auth/register-with-invitation?token=${token}`, {
                name,
                email: invitationData.email,
                password,
                organization_code: orgCode
            });
            
            localStorage.setItem('token', response.data.token);
            toast.success('Willkommen! Ihr Konto wurde erstellt.');
            navigate(`/o/${orgCode}/dashboard`);
            window.location.reload();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Registrierung fehlgeschlagen');
        } finally {
            setLoading(false);
        }
    };

    if (validating) {
        return (
            <AuthLayout>
                <Card className="border-0 shadow-lg text-center">
                    <CardContent className="pt-12 pb-8">
                        <div 
                            className="w-20 h-20 rounded-full mx-auto flex items-center justify-center mb-6"
                            style={{ backgroundColor: `${tenant?.primary_color}15` }}
                        >
                            <Loader2 
                                size={40} 
                                className="animate-spin"
                                style={{ color: tenant?.primary_color }} 
                            />
                        </div>
                        <h2 className="text-2xl font-bold text-slate-900 mb-2">
                            Einladung wird überprüft...
                        </h2>
                    </CardContent>
                </Card>
            </AuthLayout>
        );
    }

    if (!valid) {
        return (
            <AuthLayout>
                <Card className="border-0 shadow-lg text-center">
                    <CardContent className="pt-12 pb-8">
                        <div className="w-20 h-20 rounded-full mx-auto flex items-center justify-center mb-6 bg-red-50">
                            <AlertCircle size={40} className="text-red-600" />
                        </div>
                        <h2 className="text-2xl font-bold text-slate-900 mb-2">
                            Einladung ungültig
                        </h2>
                        <p className="text-slate-600 mb-6">
                            {error}
                        </p>
                        <Button
                            variant="outline"
                            onClick={() => navigate(`/o/${orgCode}/login`)}
                        >
                            Zur Anmeldung
                        </Button>
                    </CardContent>
                </Card>
            </AuthLayout>
        );
    }

    return (
        <AuthLayout>
            <Card className="border-0 shadow-lg">
                <CardHeader className="space-y-1">
                    <div className="flex items-center gap-2 mb-2">
                        <CheckCircle2 size={20} className="text-green-600" />
                        <span className="text-sm text-green-600 font-medium">Einladung gültig</span>
                    </div>
                    <CardTitle className="text-2xl font-bold">Willkommen!</CardTitle>
                    <CardDescription>
                        Sie wurden eingeladen, am Incentive-Programm von {invitationData?.organization?.name || tenant?.name} teilzunehmen. 
                        Erstellen Sie jetzt Ihr Konto.
                    </CardDescription>
                </CardHeader>
                <CardContent>
                    <form onSubmit={handleSubmit} className="space-y-4">
                        <div className="space-y-2">
                            <Label htmlFor="email">E-Mail</Label>
                            <Input
                                id="email"
                                type="email"
                                value={invitationData?.email || ''}
                                disabled
                                className="bg-slate-50"
                            />
                            <p className="text-xs text-slate-500">
                                Diese E-Mail-Adresse ist mit Ihrer Einladung verknüpft
                            </p>
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="name">Ihr Name</Label>
                            <Input
                                id="name"
                                type="text"
                                placeholder="Max Mustermann"
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                required
                                data-testid="invite-name-input"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="password">Passwort erstellen</Label>
                            <Input
                                id="password"
                                type="password"
                                placeholder="Mindestens 6 Zeichen"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                required
                                minLength={6}
                                data-testid="invite-password-input"
                            />
                        </div>
                        <Button
                            type="submit"
                            className="w-full"
                            style={{ backgroundColor: tenant?.primary_color }}
                            disabled={loading}
                            data-testid="invite-register-btn"
                        >
                            {loading ? (
                                <>
                                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                    Konto wird erstellt...
                                </>
                            ) : (
                                'Konto erstellen & starten'
                            )}
                        </Button>
                    </form>
                </CardContent>
            </Card>
        </AuthLayout>
    );
};
